<template>
  <AntTabs v-model:activeKey="activeApiTab">
    <AntTabPane key="Description" tab="Description">
      <div v-if="isDev">customizedVersionSelectedDocId: {{ customizedVersionSelectedDocId }}</div>
      <AntInputGroup compact style="margin: 8px 0 12px;">
        <AntInput
          class="prefix-input ant-col-3"
          placeholder="Switch Customized Version"
          defaultValue="Switch Customized Version"
          readonly
        />
        <CustomizedVersionSelect
          class="ant-col-21"
          :selectedDocId="customizedVersionSelectedDocId"
          :docsDataByDocId="docsDataByDocId"
          :selectedSectionState="selectedSectionState"
          :selectedSectionOrFieldState="selectedSectionState"
          @update:selectedDocId="val => customizedVersionSelectedDocId = val === 'general' ? undefined : val"
        />
      </AntInputGroup>

      <SectionEditor
        height="700px"
        :previewMode="false"
        :imageManagementData="imageManagementData"
        :sectionsDataByDocId="sectionsDataByDocId"
        :customizedVersionSelectedDocId="customizedVersionSelectedDocId || `${selectedSectionState.wallet}#master`"
        :editorContent="descriptionEditorContent"
        :onChange="val => onChangeContent(val)"
      />
    </AntTabPane>

    <AntTabPane key="Request" tab="Request" :forceRender="true">
      <FieldTable
        ref="requestFieldTableRef"
        scope="request"
        v-model:selectedExampleId="selectedExampleId"
        :isTableLoading="isTableLoading"
        :imageManagementData="imageManagementData"
        :docsDataByDocId="docsDataByDocId"
        :sectionsDataByDocId="sectionsDataByDocId"
        :selectedSectionState="selectedSectionState"
        :selectedFieldState="selectedFieldState"
        :platformOptions="platformOptions"
      />
    </AntTabPane>

    <AntTabPane key="Response" tab="Response" :forceRender="true">
      <FieldTable
        ref="responseFieldTableRef"
        scope="response"
        v-model:selectedExampleId="selectedExampleId"
        :isTableLoading="isTableLoading"
        :imageManagementData="imageManagementData"
        :docsDataByDocId="docsDataByDocId"
        :sectionsDataByDocId="sectionsDataByDocId"
        :selectedSectionState="selectedSectionState"
        :selectedFieldState="selectedFieldState"
        :platformOptions="platformOptions"
      />
    </AntTabPane>
  </AntTabs>
</template>

<script>
import {
  ref,
  reactive,
  inject,
  watch,
  computed,
  nextTick,
} from 'vue'

import { cloneDeep } from 'lodash'

import {
  Tabs as AntTabs,
  Input as AntInput,
} from 'ant-design-vue'

import SectionEditor from '@/components/ApiDoc/editor/SectionEditor.vue'
import FieldTable from '@/components/ApiDoc/editor/FieldTable.vue'
import CustomizedVersionSelect from '@/components/ApiDoc/editor/CustomizedVersionSelect.vue'

import { emptyObject } from '@/utils'
import { findExampleFormatErrorInFieldState } from './fieldExampleFormatValidation'

export default {
  name: 'ApiSection',
  components: {
    SectionEditor,
    FieldTable,
    CustomizedVersionSelect,
    AntTabs,
    AntTabPane: AntTabs.TabPane,
    AntInput,
    AntInputGroup: AntInput.Group,
  },
  props: {
    platformOptions: Array,
    imageManagementData: Array,
    docsDataByDocId: Object,
    sectionsDataByDocId: Object,
    selectedSectionState: Object,
    selectedFieldState: Object,
  },
  emits: ['change'],
  setup(props, { emit, expose }) {
    const isDev = inject('isDev')

    const activeApiTab = ref('Request')
    const requestFieldTableRef = ref(null)
    const responseFieldTableRef = ref(null)
    const selectedSectionState = reactive(props.selectedSectionState)
    const selectedFieldState = reactive(props.selectedFieldState)

    const isTableLoading = ref(false)

    const getSuccessExampleId = () => {
      const examples = selectedSectionState.examples || {}
      return Object.values(examples).find(({ exampleName }) => exampleName && exampleName.includes('success'))?.exampleId
        || Object.keys(examples)[0]
    }

    const selectedExampleId = ref(getSuccessExampleId())

    watch(
      () => Object.keys(selectedSectionState.examples || {}),
      (keys) => {
        if (selectedExampleId.value && !keys.includes(selectedExampleId.value)) {
          selectedExampleId.value = getSuccessExampleId()
        }
      },
    )

    watch(
      [
        // () => props.rerenderChildTable,
        () => props.selectedSectionState.docId,
        () => props.selectedSectionState.wallet,
        () => props.selectedSectionState.sectionId,
        () => props.platformOptions,
      ],
      async ([newDocId, newWallet, newSectionId, newPlatformOptions], [oldDocId, oldWallet, oldSectionId, oldPlatformOptions]) => {
        console.log('[ApiSection] watch selectedSectionState.docId & wallet & sectionId & platformOptions', {
          newDocId,
          oldDocId,
          newWallet,
          oldWallet,
          newSectionId,
          oldSectionId,
          newPlatformOptions,
          oldPlatformOptions,
          'props.selectedSectionState': cloneDeep(props.selectedSectionState),
        })

        if (props.selectedSectionState.sectionType !== 'api'
          || (newWallet === oldWallet && newDocId === oldDocId && newSectionId === oldSectionId)
          || !newDocId || !newWallet || !newSectionId) return

        const clonedSelectedSectionState = cloneDeep(props.selectedSectionState) // 一定要先 clone 避免被 emptyObject 清空
        emptyObject(selectedSectionState)
        Object.assign(selectedSectionState, clonedSelectedSectionState)

        const clonedSelectedFieldState = cloneDeep(props.selectedFieldState) // 一定要先 clone 避免被 emptyObject 清空
        emptyObject(selectedFieldState)
        Object.assign(selectedFieldState, clonedSelectedFieldState)

        selectedExampleId.value = getSuccessExampleId()
      },
      { immediate: true },
    )

    const getExampleFormatErrors = () => findExampleFormatErrorInFieldState({
      selectedFieldState,
      selectedSectionState,
    })

    const hasExampleFormatError = () => {
      const { requestErr, responseErr } = getExampleFormatErrors()
      return !!(requestErr || responseErr)
    }

    const waitForScopeTabPaneActive = async (scope, maxMs = 4000) => {
      const deadline = Date.now() + maxMs
      while (Date.now() < deadline) {
        const root = document.querySelector(`[data-field-table-scope="${scope}"]`)
        const pane = root?.closest('.ant-tabs-tabpane')
        const rect = root?.getBoundingClientRect()
        if (
          root
          && (!pane || pane.classList.contains('ant-tabs-tabpane-active'))
          && root.offsetParent !== null
          && rect?.width > 0
          && rect?.height > 0
        ) {
          return true
        }
        await nextTick()
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
      return false
    }

    const waitForFieldTableRef = async (getRef, maxMs = 3000) => {
      const deadline = Date.now() + maxMs
      while (Date.now() < deadline) {
        const tableRef = getRef()
        if (tableRef?.focusExampleFormatErrorInput) return tableRef
        await nextTick()
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
      return null
    }

    const focusExampleFormatErrorInScope = async (scope, err, getRef) => {
      activeApiTab.value = scope === 'request' ? 'Request' : 'Response'
      await nextTick()
      await nextTick()
      await waitForScopeTabPaneActive(scope)
      await new Promise(resolve => setTimeout(resolve, 150))
      const tableRef = await waitForFieldTableRef(getRef)
      if (tableRef?.focusExampleFormatErrorInput) await tableRef.focusExampleFormatErrorInput(err)
    }

    const focusExampleFormatErrorIfNeeded = async () => {
      const { requestErr, responseErr } = getExampleFormatErrors()
      if (requestErr) {
        await focusExampleFormatErrorInScope('request', requestErr, () => requestFieldTableRef.value)
        return true
      }
      if (responseErr) {
        await focusExampleFormatErrorInScope('response', responseErr, () => responseFieldTableRef.value)
        return true
      }
      return false
    }

    const refocusExampleFormatErrorIfNeeded = async () => {
      const { requestErr, responseErr } = getExampleFormatErrors()
      if (requestErr) {
        const tableRef = await waitForFieldTableRef(() => requestFieldTableRef.value)
        if (tableRef?.refocusExampleFormatErrorInput) return tableRef.refocusExampleFormatErrorInput()
      }
      if (responseErr) {
        const tableRef = await waitForFieldTableRef(() => responseFieldTableRef.value)
        if (tableRef?.refocusExampleFormatErrorInput) return tableRef.refocusExampleFormatErrorInput()
      }
      return false
    }

    expose({
      hasExampleFormatError,
      focusExampleFormatErrorIfNeeded,
      refocusExampleFormatErrorIfNeeded,
    })

    const platformOptionsById = computed(() => Object.fromEntries(props.platformOptions.map(option => [option.value, option])))
    const platformOptions = computed(() => {
      if (selectedSectionState.platforms) {
        return selectedSectionState.platforms.reduce((acc, id) => {
          const option = platformOptionsById.value[id]
          if (option) acc.push(option)
          return acc
        }, [])
      }
      return props.platformOptions
    })

    const customizedVersionSelectedDocId = ref(undefined)

    /** General 用 section.content；子本僅在 customizedVersion[docId] 有 content 欄位時才顯示，不 fallback 避免誤以為已客製 */
    const descriptionEditorContent = computed(() => {
      const docId = customizedVersionSelectedDocId.value
      if (!docId) return selectedSectionState.content
      const cv = selectedSectionState.customizedVersion?.[docId]
      if (cv != null && Object.prototype.hasOwnProperty.call(cv, 'content')) {
        return cv.content
      }
      return undefined
    })

    // 點擊 Cancel 按鈕，selectedDocId 會變回 general
    watch(() => Object.keys(selectedSectionState.customizedVersion || {}), (newVal, oldVal) => {
      console.log('[AppendixSection] Object.keys(selectedSectionState.customizedVersion)', newVal, oldVal)
      if (!newVal.includes(customizedVersionSelectedDocId.value)) customizedVersionSelectedDocId.value = undefined
    })

    const onChangeContent = (val) => {
      const docId = customizedVersionSelectedDocId.value
      if (docId) {
        if (!selectedSectionState.customizedVersion) selectedSectionState.customizedVersion = {}
        if (!selectedSectionState.customizedVersion[docId]) {
          selectedSectionState.customizedVersion[docId] = { docId }
        }
        selectedSectionState.customizedVersion[docId].content = val
      } else {
        selectedSectionState.content = val
      }
    }

    return {
      isDev,
      activeApiTab,
      requestFieldTableRef,
      responseFieldTableRef,
      selectedExampleId,
      isTableLoading,
      platformOptions, // eslint-disable-line vue/no-dupe-keys
      selectedSectionState, // eslint-disable-line vue/no-dupe-keys
      selectedFieldState, // eslint-disable-line vue/no-dupe-keys
      customizedVersionSelectedDocId,
      descriptionEditorContent,
      onChangeContent,
      emit,
    }
  },
}
</script>
